import React from "react";
import {
  Typography,
  List,
  ListItemText,
  CircularProgress,
  ListItemButton,
} from "@mui/material";
import { FormikPayment } from "utils/payments/paymentFormat";
import { formatCashFull } from "utils/formatCash";
import { paymentStatusLabelKey } from "utils/payments/paymentStatus";
import { paymentMethodLabelKey } from "utils/payments/paymentMethod";
import ViewPaymentDetailsDrawer from "./ViewPaymentDetailsDrawer";
import { useTranslation } from "react-i18next";
import { useGetLatestPayments } from "hooks/useGetLatestPayments";
import { Avatar, ListItemIcon } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import CustomSkeleton from "components/common/CustomSkeleton";

interface LatestPaymentsProps {
  /** Omit where the surface already heads this section itself. */
  title?: string;
  description?: string;
  contactId?: string;
  onSelectPayment?: (payment: FormikPayment) => void;
  /** Replaces the default "No recent payments found" line, for a surface with its own empty-state style. */
  emptyState?: React.ReactNode;
}

const LatestPayments: React.FC<LatestPaymentsProps> = (props) => {
  const { title, description, contactId, onSelectPayment, emptyState } = props;
  const { t } = useTranslation();

  const { latestPayments, loadingLatestPayments } =
    useGetLatestPayments(contactId);

  const payments = latestPayments || [];

  const [selectedPaymentId, setSelectedPaymentId] = React.useState<string>("");

  function getDisplayDate(payment: FormikPayment) {
    if (payment.actualPaymentDate)
      return payment.actualPaymentDate.toLocaleDateString();
    if (payment.scheduledPaymentDate)
      return payment.scheduledPaymentDate.toLocaleDateString();
    return "-";
  }

  const content = (
    <Stack gap="var(--space-1)">
      {title && (
        <Inline gap="var(--space-3)" wrap={false}>
          <Typography variant="h6">{title}</Typography>
          {loadingLatestPayments && <CircularProgress size={16} />}
        </Inline>
      )}
      {description && (
        <Typography
          variant="body2"
          sx={{
            color: "var(--color-text-tertiary)",
          }}
        >
          {t(description)}
        </Typography>
      )}
      {payments.length === 0 ? (
        loadingLatestPayments ? (
          <CustomSkeleton variant="list" rows={3} />
        ) : (
          (emptyState ?? (
            <Typography
              variant="body2"
              sx={{
                color: "var(--color-text-secondary)",
                marginBlockEnd: "var(--space-2)",
              }}
            >
              {t("No recent payments found")}
            </Typography>
          ))
        )
      ) : (
        <>
          <List sx={{ marginTop: title || description ? "var(--space-4)" : 0 }}>
            {payments.map((payment, idx) => (
              <ListItemButton
                key={payment.id ? payment.id : `payment-${idx}`}
                onClick={
                  onSelectPayment
                    ? () => onSelectPayment(payment)
                    : () => setSelectedPaymentId(payment.id || "")
                }
              >
                <ListItemIcon>
                  <Avatar color="default" name={payment.payTo?.name} />
                </ListItemIcon>
                <ListItemText
                  sx={{ paddingX: "var(--space-2)" }}
                  primary={
                    <Inline component="span" gap="var(--space-3)">
                      <Typography component="span" variant="subtitle2">
                        {payment.payTo?.name || t("Unknown Contact")}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: "var(--color-text-tertiary)",
                        }}
                      >
                        {payment.status &&
                          t(paymentStatusLabelKey(payment.status))}
                      </Typography>
                    </Inline>
                  }
                  secondary={
                    <Inline component="span" gap="var(--space-2)">
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: "var(--color-text-brand)" }}
                      >
                        {formatCashFull(payment.amount, payment.currency)}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          color: "var(--color-text-secondary)",
                        }}
                      >
                        &bull;{" "}
                        {t(
                          paymentMethodLabelKey(payment.paymentMethod) ||
                            "Unknown Method",
                        )}{" "}
                        &bull; {getDisplayDate(payment)}
                      </Typography>
                    </Inline>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </>
      )}

      <ViewPaymentDetailsDrawer
        open={!!selectedPaymentId}
        paymentId={selectedPaymentId}
        handleClose={() => setSelectedPaymentId("")}
      />
    </Stack>
  );

  return content;
};

export default LatestPayments;
